import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Input,
  ScreenLoader,
  Select,
  Spinner,
  StatusBadge,
  Card,
  Textarea,
  CategoryBadge,
  SectionDivider,
  HighlightCard,
  StopCard,
  HostNote,
  Tab,
} from '../../components/design-system';
import { MessageBubble } from '../../components/alma/MessageBubble';
import { OAuthButtons } from '../../components/auth/OAuthButtons';
import { useDesignAdmin } from '../../hooks/useDesignTheme';
import {
  DEFAULT_THEME,
  applyTheme,
  exportTheme,
  exportThemeCss,
  looksLikeTheme,
  normalizeTheme,
  parseCssToTheme,
  type DesignTheme,
  type Accents,
  type SemanticColors,
  type Radii,
  type Fonts,
} from '../../lib/design/theme';
import {
  createPreset,
  deletePreset,
  ensureDefaultPreset,
  setActivePreset,
} from '../../lib/design/store';

// ─── Helpers ────────────────────────────────────────────────────────────────

function deepEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function getChangedTokens(saved: DesignTheme, draft: DesignTheme): { path: string; from: string; to: string }[] {
  const changes: { path: string; from: string; to: string }[] = [];
  function walk(prefix: string, a: Record<string, unknown>, b: Record<string, unknown>) {
    for (const key of Object.keys(a)) {
      const va = a[key], vb = b[key];
      if (typeof va === 'object' && va && typeof vb === 'object' && vb) {
        walk(`${prefix}.${key}`, va as Record<string, unknown>, vb as Record<string, unknown>);
      } else if (va !== vb) {
        changes.push({ path: `${prefix}.${key}`, from: String(va), to: String(vb) });
      }
    }
  }
  walk('', saved as unknown as Record<string, unknown>, draft as unknown as Record<string, unknown>);
  return changes;
}

// ─── Token editor sub-components ────────────────────────────────────────────

function ColorToken({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const isRgba = value.startsWith('rgba');
  return (
    <div className="flex items-center gap-3">
      <div className="relative w-9 h-9 shrink-0">
        <div className="w-9 h-9 rounded-lg border border-app-border overflow-hidden" style={{ background: value }} />
        {!isRgba && (
          <input
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-app-fg truncate">{label}</p>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="text-[11px] font-mono text-app-muted bg-transparent border-none p-0 focus:outline-none w-full"
        />
      </div>
    </div>
  );
}

function RadiusToken({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const num = parseInt(value) || 0;
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="w-12 h-12 bg-app-bg border-2 border-app-border" style={{ borderRadius: value }} />
      <span className="text-[10px] text-app-muted">{label}</span>
      <input
        type="number"
        min={0}
        max={999}
        value={num}
        onChange={(e) => onChange(`${e.target.value}px`)}
        className="w-16 text-center text-xs font-mono rounded-md border border-app-border bg-app-bg px-1 py-1 text-app-fg focus:border-gold focus:outline-none"
      />
    </div>
  );
}

// ─── Section wrapper ────────────────────────────────────────────────────────

function Section({ title, description, children, defaultOpen = true }: { title: string; description?: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-app-card border border-app-border rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-5 py-4 flex items-center justify-between gap-3 text-left hover:bg-black/[0.02] transition-colors"
      >
        <div>
          <h3 className="text-sm font-semibold text-app-fg">{title}</h3>
          {description && <p className="text-xs text-app-muted mt-0.5">{description}</p>}
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className={`w-4 h-4 text-app-muted transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>
      {open && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
}

// ─── Component catalog items ────────────────────────────────────────────────

function CatalogItem({ name, notes, children }: { name: string; notes?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      <div>
        <h4 className="text-sm font-semibold text-app-fg">{name}</h4>
        {notes && <p className="text-[11px] text-app-muted mt-0.5">{notes}</p>}
      </div>
      <div className="bg-app-bg border border-app-border rounded-xl p-5">
        {children}
      </div>
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────

export function DesignSettings() {
  const { presets, active, loading, reload } = useDesignAdmin();
  const [draft, setDraft] = useState<DesignTheme>(DEFAULT_THEME);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [showDiff, setShowDiff] = useState(false);
  const [activeTab, setActiveTab] = useState<'tokens' | 'components' | 'presets' | 'docs'>('tokens');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => { void ensureDefaultPreset(); }, []);
  useEffect(() => { setDraft(active); }, [active]);

  // Apply draft to live preview whenever it changes
  useEffect(() => { applyTheme(draft); }, [draft]);

  const isDirty = !deepEqual(active, draft);
  const changes = isDirty ? getChangedTokens(active, draft) : [];

  function flash(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }

  function resetDraft() {
    setDraft(active);
    applyTheme(active);
    flash('Reset to saved values');
  }

  // ── Updaters ────────────────────────────────────────────────────────────

  function updateAccent<K extends keyof Accents>(key: K, value: string) {
    setDraft(d => ({ ...d, accents: { ...d.accents, [key]: value } }));
  }
  function updateLight<K extends keyof SemanticColors>(key: K, value: string) {
    setDraft(d => ({ ...d, light: { ...d.light, [key]: value } }));
  }
  function updateDark<K extends keyof SemanticColors>(key: K, value: string) {
    setDraft(d => ({ ...d, dark: { ...d.dark, [key]: value } }));
  }
  function updateRadius<K extends keyof Radii>(key: K, value: string) {
    setDraft(d => ({ ...d, radii: { ...d.radii, [key]: value } }));
  }
  function updateFont<K extends keyof Fonts>(key: K, value: string) {
    setDraft(d => ({ ...d, fonts: { ...d.fonts, [key]: value } }));
  }
  function updateName(name: string) {
    setDraft(d => ({ ...d, name }));
  }

  // ── Save / Import ───────────────────────────────────────────────────────

  async function handleSave() {
    setBusy('save');
    setError(null);
    const created = await createPreset(draft.name, draft, true);
    await reload();
    setBusy(null);
    setShowDiff(false);
    if (created) flash('Saved & applied'); else setError('Could not save theme.');
  }

  async function handleApply(id: string) {
    setBusy(id);
    setError(null);
    const ok = await setActivePreset(id);
    await reload();
    setBusy(null);
    if (ok) flash('Theme applied'); else setError('Could not apply theme.');
  }

  async function handleDelete(id: string) {
    setBusy(id);
    await deletePreset(id);
    await reload();
    setBusy(null);
    flash('Preset removed');
  }

  async function handleResetDefault() {
    setBusy('reset');
    setError(null);
    const created = await createPreset(DEFAULT_THEME.name, DEFAULT_THEME, true);
    await reload();
    setBusy(null);
    if (created) flash('Default theme restored'); else setError('Could not restore default.');
  }

  async function handleImportFile(file: File) {
    setError(null);
    try {
      const text = await file.text();
      const isCss = file.name.endsWith('.css');
      let theme: DesignTheme | null = null;
      if (isCss) {
        theme = parseCssToTheme(text, file.name.replace(/\.css$/i, ''));
        if (!theme) { setError('Could not find CSS custom properties in that file.'); return; }
      } else {
        const parsed = JSON.parse(text);
        if (!looksLikeTheme(parsed)) { setError('That file does not look like a Sekretly theme.'); return; }
        theme = normalizeTheme(parsed, file.name.replace(/\.json$/i, ''));
      }
      setDraft(theme);
      flash(`Loaded "${theme.name}" — preview active, save to commit`);
    } catch {
      setError('Could not read that file — is it valid JSON or CSS?');
    }
  }

  // ── Tab content ─────────────────────────────────────────────────────────

  const SEMANTIC_LABELS: Record<keyof SemanticColors, string> = {
    bg: 'Background', bgDeep: 'Deep BG', surface: 'Surface / Card',
    text: 'Text', textSoft: 'Text soft', textDim: 'Text dim / Muted',
    line: 'Line / Border', bubbleYou: 'Chat bubble (you)', eyebrow: 'Eyebrow accent',
  };

  const ACCENT_LABELS: Record<keyof Accents, string> = {
    gold: 'Gold (primary CTA)', violet: 'Violet', crimson: 'Crimson',
    success: 'Success / Green', ink: 'Ink (darkest)', cream: 'Cream (lightest)',
    onGold: 'Text on gold',
  };

  const FONT_OPTIONS = [
    '"Hedvig Letters Serif", Georgia, serif',
    '"Inter Tight", system-ui, -apple-system, sans-serif',
    '"Playfair Display", Georgia, serif',
    '"DM Serif Display", Georgia, serif',
    '"Lora", Georgia, serif',
    '"Work Sans", system-ui, sans-serif',
    '"DM Sans", system-ui, sans-serif',
    '"Outfit", system-ui, sans-serif',
    'Georgia, serif',
    'system-ui, sans-serif',
  ];

  if (loading) {
    return (
      <div className="min-h-dvh bg-app-bg text-app-fg">
        <div className="px-6 py-6">
          <h1 className="text-2xl md:text-3xl font-semibold text-app-fg mb-1">Design System</h1>
          <div className="flex justify-center py-16"><Spinner /></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-app-bg text-app-fg">
      <div className="px-6 py-6 flex flex-col gap-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-app-fg mb-1">Design System</h1>
            <p className="text-sm text-app-muted leading-relaxed max-w-xl">
              Edit tokens live, preview how components respond, then save when it looks right.
              Changes apply instantly to every user.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isDirty && (
              <StatusBadge tone="gold" label={`${changes.length} unsaved ${changes.length === 1 ? 'change' : 'changes'}`} />
            )}
          </div>
        </div>

        {error && <p className="text-sm text-crimson">{error}</p>}
        {toast && <p className="text-sm text-success font-medium">{toast}</p>}

        {/* Sticky action bar */}
        <div className="sticky top-0 z-30 bg-app-bg/80 backdrop-blur-md -mx-6 px-6 py-3 border-b border-app-border flex items-center gap-3 flex-wrap">
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json,.css,text/css"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleImportFile(f); e.target.value = ''; }}
          />
          <Button size="sm" variant="secondary" onClick={() => fileRef.current?.click()} disabled={busy !== null}>
            Import file…
          </Button>
          <Button size="sm" variant="secondary" onClick={() => exportTheme(draft)}>
            Export JSON
          </Button>
          <Button size="sm" variant="secondary" onClick={() => exportThemeCss(draft)}>
            Export CSS
          </Button>
          <div className="flex-1" />
          {isDirty && (
            <>
              <Button size="sm" variant="ghost" onClick={resetDraft}>
                Reset
              </Button>
              <Button size="sm" onClick={() => setShowDiff(true)} disabled={busy !== null}>
                {busy === 'save' ? <Spinner size="sm" className="text-ink" /> : `Save ${changes.length} changes`}
              </Button>
            </>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          {(['tokens', 'components', 'presets', 'docs'] as const).map(tab => (
            <Tab
              key={tab}
              active={activeTab === tab}
              onClick={() => setActiveTab(tab)}
              className="capitalize"
              style={{ flex: 1 }}
            >
              {tab}
            </Tab>
          ))}
        </div>

        {/* ═══ TOKENS TAB ═══════════════════════════════════════════════════ */}
        {activeTab === 'tokens' && (
          <div className="flex flex-col gap-4">
            {/* Theme name */}
            <div className="bg-app-card border border-app-border rounded-2xl p-5">
              <Input
                label="Theme name"
                value={draft.name}
                onChange={(e) => updateName(e.target.value)}
                placeholder="My custom theme"
              />
            </div>

            {/* Accents */}
            <Section title="Accent Colors" description="Brand colors used across both light and dark modes.">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {(Object.keys(draft.accents) as (keyof Accents)[]).map(key => (
                  <ColorToken
                    key={key}
                    label={ACCENT_LABELS[key]}
                    value={draft.accents[key]}
                    onChange={(v) => updateAccent(key, v)}
                  />
                ))}
              </div>
            </Section>

            {/* Light mode */}
            <Section title="Light Mode" description="Semantic colors for the default (light) appearance.">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {(Object.keys(draft.light) as (keyof SemanticColors)[]).map(key => (
                  <ColorToken
                    key={key}
                    label={SEMANTIC_LABELS[key]}
                    value={draft.light[key]}
                    onChange={(v) => updateLight(key, v)}
                  />
                ))}
              </div>
            </Section>

            {/* Dark mode */}
            <Section title="Dark Mode" description="Semantic colors for dark appearance.">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {(Object.keys(draft.dark) as (keyof SemanticColors)[]).map(key => (
                  <ColorToken
                    key={key}
                    label={SEMANTIC_LABELS[key]}
                    value={draft.dark[key]}
                    onChange={(v) => updateDark(key, v)}
                  />
                ))}
              </div>
            </Section>

            {/* Radii */}
            <Section title="Border Radius" description="Shape tokens — corners of cards, buttons, pills.">
              <div className="flex items-end gap-6 flex-wrap">
                {(Object.keys(draft.radii) as (keyof Radii)[]).map(key => (
                  <RadiusToken
                    key={key}
                    label={key}
                    value={draft.radii[key]}
                    onChange={(v) => updateRadius(key, v)}
                  />
                ))}
              </div>
            </Section>

            {/* Fonts */}
            <Section title="Typography" description="Font families for headings and body text.">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-app-fg-2">Serif (headings)</label>
                  <select
                    value={draft.fonts.serif}
                    onChange={(e) => updateFont('serif', e.target.value)}
                    className="w-full appearance-none rounded-md border border-app-border bg-app-bg px-3 py-2.5 text-sm text-app-fg focus:border-gold focus:outline-none"
                  >
                    {FONT_OPTIONS.map(f => <option key={f} value={f}>{f.split(',')[0].replace(/"/g, '')}</option>)}
                  </select>
                  <p className="text-xl text-app-fg mt-2" style={{ fontFamily: draft.fonts.serif }}>
                    The quick brown fox jumps over the lazy dog
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-app-fg-2">Sans (body & UI)</label>
                  <select
                    value={draft.fonts.sans}
                    onChange={(e) => updateFont('sans', e.target.value)}
                    className="w-full appearance-none rounded-md border border-app-border bg-app-bg px-3 py-2.5 text-sm text-app-fg focus:border-gold focus:outline-none"
                  >
                    {FONT_OPTIONS.map(f => <option key={f} value={f}>{f.split(',')[0].replace(/"/g, '')}</option>)}
                  </select>
                  <p className="text-sm text-app-fg mt-2" style={{ fontFamily: draft.fonts.sans }}>
                    The quick brown fox jumps over the lazy dog — 0123456789
                  </p>
                </div>
              </div>
            </Section>
          </div>
        )}

        {/* ═══ COMPONENTS TAB ═══════════════════════════════════════════════ */}
        {activeTab === 'components' && (
          <div className="flex flex-col gap-6">
            <p className="text-sm text-app-muted">
              Real production components rendered with current token values. Edit tokens to see changes reflected here in real time.
            </p>

            {/* Button */}
            <Section title="Primitives" description="Core UI building blocks used across the app.">
              <div className="flex flex-col gap-6">
                <CatalogItem name="Button" notes="Variants: primary, secondary, ghost × Sizes: sm, md, lg. No destructive/danger variant exists.">
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <Button variant="primary" size="sm">Primary sm</Button>
                      <Button variant="primary" size="md">Primary md</Button>
                      <Button variant="primary" size="lg">Primary lg</Button>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <Button variant="secondary" size="sm">Secondary sm</Button>
                      <Button variant="secondary" size="md">Secondary md</Button>
                      <Button variant="secondary" size="lg">Secondary lg</Button>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <Button variant="ghost" size="sm">Ghost sm</Button>
                      <Button variant="ghost" size="md">Ghost md</Button>
                      <Button variant="ghost" size="lg">Ghost lg</Button>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <Button disabled>Disabled primary</Button>
                      <Button variant="secondary" disabled>Disabled secondary</Button>
                      <Button variant="ghost" disabled>Disabled ghost</Button>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <Button><Spinner size="sm" className="text-ink mr-2" />Loading…</Button>
                    </div>
                  </div>
                </CatalogItem>

                <CatalogItem name="Input" notes="With label, without label, error state, placeholder, disabled.">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
                    <Input label="Full name" placeholder="Maria da Silva" />
                    <Input label="Email" placeholder="you@example.com" value="mauro@sekretly.com" readOnly />
                    <Input label="With error" placeholder="Enter something" error="This field is required" />
                    <Input placeholder="No label — just placeholder" />
                    <Input label="Disabled" placeholder="Can't touch this" disabled />
                    <Input label="Long value overflow test" value="This is a very long value that should demonstrate how the input handles text overflow in a constrained width" readOnly />
                  </div>
                </CatalogItem>

                <CatalogItem name="Select" notes="Dropdown with label, placeholder, error state.">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
                    <Select label="Country" options={['Portugal', 'Brazil', 'Spain', 'Italy', 'France']} placeholder="Choose…" />
                    <Select label="With error" options={['Option A', 'Option B']} error="Please select one" placeholder="Select…" />
                  </div>
                </CatalogItem>

                <CatalogItem name="Spinner" notes="Inline loading indicator in sm, md, lg — uses currentColor, for buttons and section loads.">
                  <div className="flex items-center gap-6">
                    <div className="flex flex-col items-center gap-1">
                      <Spinner size="sm" />
                      <span className="text-[10px] text-app-muted">sm</span>
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <Spinner size="md" />
                      <span className="text-[10px] text-app-muted">md</span>
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <Spinner size="lg" />
                      <span className="text-[10px] text-app-muted">lg</span>
                    </div>
                  </div>
                </CatalogItem>

                <CatalogItem name="ScreenLoader" notes="Branded Sekretly S-mark animation, theme-aware. Full screen and page-level loads only.">
                  <ScreenLoader />
                </CatalogItem>
              </div>
            </Section>

            {/* Product components */}
            <Section title="Product Components" description="Domain-specific components used across the app.">
              <div className="flex flex-col gap-6">
                <CatalogItem name="MessageBubble" notes="Chat bubbles for Alma conversations — user (right) and assistant (left) with reaction picker.">
                  <div className="flex flex-col gap-2 max-w-lg">
                    <MessageBubble
                      message={{ role: 'assistant', content: "Tell me about a trip that felt exactly right — where you didn't want to leave. What made it special?", timestamp: new Date().toISOString() }}
                      index={0}
                      onReact={() => {}}
                    />
                    <MessageBubble
                      message={{ role: 'user', content: "There was this tiny town on the Amalfi Coast — Atrani. No tourists, just locals having espresso by the water. We stumbled on it by accident and stayed three extra days.", timestamp: new Date().toISOString() }}
                      index={1}
                      onReact={() => {}}
                      reaction="❤️"
                    />
                    <MessageBubble
                      message={{ role: 'assistant', content: "Atrani — that's exactly the kind of place I'd hoped you'd mention. The kind you find by accident and remember forever.\n\nWhat was it about the pace there? Were you slowing down, or did the place just match how you were already moving?", timestamp: new Date().toISOString() }}
                      index={2}
                      onReact={() => {}}
                    />
                  </div>
                </CatalogItem>

                <CatalogItem name="OAuth Buttons" notes="Social login buttons — Google (active) and Apple (coming soon / disabled).">
                  <div className="max-w-sm">
                    <OAuthButtons />
                  </div>
                </CatalogItem>
              </div>
            </Section>

            {/* Token-driven showcase */}
            <Section title="Token Showcase" description="Examples showing how tokens render in context — useful for catching issues when editing.">
              <div className="flex flex-col gap-6">
                <CatalogItem name="Color Scale" notes="All accent and semantic colors as rendered.">
                  <div className="flex flex-col gap-4">
                    <div>
                      <p className="text-xs text-app-muted mb-2 uppercase tracking-wide font-semibold">Accents</p>
                      <div className="flex gap-2 flex-wrap">
                        {(Object.entries(draft.accents) as [string, string][]).map(([name, val]) => (
                          <div key={name} className="flex flex-col items-center gap-1">
                            <div className="w-12 h-12 rounded-xl border border-app-border" style={{ background: val }} />
                            <span className="text-[10px] text-app-muted">{name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-app-muted mb-2 uppercase tracking-wide font-semibold">Semantic (current mode)</p>
                      <div className="flex gap-2 flex-wrap">
                        {(Object.entries(draft.light) as [string, string][]).map(([name, val]) => (
                          <div key={name} className="flex flex-col items-center gap-1">
                            <div className="w-12 h-12 rounded-xl border border-app-border" style={{ background: val }} />
                            <span className="text-[10px] text-app-muted">{name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CatalogItem>

                <CatalogItem name="Typography Sample" notes="Heading and body fonts at various sizes.">
                  <div className="flex flex-col gap-3">
                    <h1 className="text-3xl">Heading 1 — Serif</h1>
                    <h2 className="text-2xl">Heading 2 — Serif</h2>
                    <h3 className="text-xl">Heading 3 — Serif</h3>
                    <h4 className="text-lg">Heading 4 — Serif</h4>
                    <p className="text-base text-app-fg">Body — The quick brown fox jumps over the lazy dog. 0123456789</p>
                    <p className="text-sm text-app-fg-2">Small — The quick brown fox jumps over the lazy dog.</p>
                    <p className="text-xs text-app-muted">Muted — Supporting text or captions.</p>
                  </div>
                </CatalogItem>

                <CatalogItem name="Radius Preview" notes="How current radii render on cards and pills.">
                  <div className="flex items-end gap-4 flex-wrap">
                    {(Object.entries(draft.radii) as [string, string][]).map(([name, val]) => (
                      <div key={name} className="flex flex-col items-center gap-2">
                        <div className="w-20 h-14 bg-gold/20 border-2 border-gold/40" style={{ borderRadius: val }} />
                        <span className="text-[10px] font-mono text-app-muted">{name}: {val}</span>
                      </div>
                    ))}
                  </div>
                </CatalogItem>

                <CatalogItem name="Buttons" notes="Button component — primary / secondary / ghost, sizes sm/md/lg.">
                  <div className="flex flex-wrap items-center gap-3">
                    <Button size="sm">Primary</Button>
                    <Button size="sm" variant="secondary">Secondary</Button>
                    <Button size="sm" variant="ghost">Ghost</Button>
                    <Button size="md">Medium</Button>
                    <Button size="sm" disabled>Disabled</Button>
                  </div>
                </CatalogItem>

                <CatalogItem name="Status badges" notes="StatusBadge — semantic colours from lib/statusChip.ts, with dot.">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge status="pending" />
                    <StatusBadge status="proposed" />
                    <StatusBadge status="accepted" />
                    <StatusBadge status="declined" />
                    <StatusBadge status="completed" />
                    <StatusBadge tone="success" label="Approved" />
                  </div>
                </CatalogItem>

                <CatalogItem name="Category badges" notes="CategoryBadge — neutral inline pill for tags and categories.">
                  <div className="flex flex-wrap items-center gap-2">
                    <CategoryBadge label="Food" />
                    <CategoryBadge label="Nature" />
                    <CategoryBadge label="Landmark" />
                    <CategoryBadge label="Art" />
                  </div>
                </CatalogItem>

                <CatalogItem name="Section divider" notes="SectionDivider — labelled rule with a gold accent icon.">
                  <SectionDivider label="Around lunch" icon="coffee" />
                </CatalogItem>

                <CatalogItem name="Itinerary cards" notes="HighlightCard (featured) and StopCard (regular stop).">
                  <div className="flex flex-col gap-3">
                    <HighlightCard
                      title="Miradouro da Senhora do Monte"
                      description="The widest sunset view in the city, away from the crowds."
                      category="View"
                      gradient="linear-gradient(150deg,#3d4d70,#1a2030)"
                      icon="mountain"
                    />
                    <StopCard
                      title="Time Out Market"
                      tip="Go early to beat the lunch rush; the seafood counters are the standout."
                      category="Food"
                      gradient="linear-gradient(150deg,#8a5b30,#33251a)"
                      icon="tools-kitchen-2"
                    />
                  </div>
                </CatalogItem>

                <CatalogItem name="Host note" notes="HostNote — amber-tinted personal message from the host.">
                  <HostNote hostName="Marta" quote="I've left the mornings light so you can wander — the city rewards an unhurried pace." />
                </CatalogItem>

                <CatalogItem name="Form controls" notes="Input, Select, Textarea — shared field components.">
                  <div className="flex flex-col gap-3">
                    <Input label="Name" placeholder="e.g. Marta" />
                    <Select label="Audience" options={[{ value: 'both', label: 'Both' }, { value: 'traveler', label: 'Traveler only' }, { value: 'host', label: 'Host only' }]} />
                    <Textarea label="Notes" placeholder="A few words…" rows={2} />
                  </div>
                </CatalogItem>

                <CatalogItem name="Card Pattern" notes="Surface + border + radius as used across the app (Card component).">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Card className="p-5">
                      <h3 className="text-base font-serif text-app-fg mb-1">Card heading</h3>
                      <p className="text-sm text-app-fg-2">Body text on a card surface. This uses the surface token for background, the line token for borders.</p>
                      <div className="mt-3 flex gap-2">
                        <Button size="sm">Action</Button>
                        <Button size="sm" variant="ghost">Cancel</Button>
                      </div>
                    </Card>
                    <Card className="p-5">
                      <p className="text-xs uppercase tracking-wide text-app-muted font-semibold mb-2">Stats card</p>
                      <p className="text-3xl font-serif text-app-fg">1,247</p>
                      <p className="text-sm text-app-muted mt-1">Total conversations this month</p>
                    </Card>
                  </div>
                </CatalogItem>
              </div>
            </Section>

            {/* Skipped components note */}
            <Card className="p-5">
              <h3 className="text-sm font-semibold text-app-fg mb-2">Components not shown here</h3>
              <p className="text-xs text-app-muted leading-relaxed">
                <strong>AddressInput</strong> — requires live geocoding API. <strong>ZoneMap</strong> — requires Leaflet map tiles.
                <strong> FocalPointPicker</strong> — requires an image URL. <strong>MoodboardGrid</strong> — requires image URLs.
                <strong> Full modals</strong> (FaqModal, SupportModal, AlmaIntroModal) — require overlay/portal context that conflicts with catalog layout.
                These components consume the same design tokens and will respond to theme changes when used in their normal context.
              </p>
            </Card>
          </div>
        )}

        {/* ═══ PRESETS TAB ══════════════════════════════════════════════════ */}
        {activeTab === 'presets' && (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Button size="sm" variant="ghost" onClick={() => void handleResetDefault()} disabled={busy !== null}>
                {busy === 'reset' ? <Spinner size="sm" /> : 'Restore default'}
              </Button>
            </div>

            {presets.length === 0 && <p className="text-sm text-app-muted">No presets yet.</p>}

            <div className="flex flex-col gap-2">
              {presets.map((p) => (
                <div key={p.id} className="bg-app-card border border-app-border rounded-xl px-4 py-3 flex items-center gap-3">
                  <div className="flex items-center gap-1.5 shrink-0">
                    {(['gold', 'violet', 'crimson', 'success'] as const).map((k) => (
                      <span key={k} className="w-3.5 h-3.5 rounded-full border border-app-border" style={{ background: p.theme.accents[k] }} />
                    ))}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-app-fg truncate">{p.theme.name || p.name}</p>
                    <p className="text-[10px] text-app-muted">{new Date(p.created_at).toLocaleDateString()}</p>
                  </div>
                  {p.is_active ? (
                    <StatusBadge tone="success" label="Active" className="shrink-0" />
                  ) : (
                    <Button size="sm" variant="ghost" className="px-2 py-1 text-xs" disabled={busy !== null}
                      onClick={() => void handleApply(p.id)}>
                      {busy === p.id ? <Spinner size="sm" /> : 'Apply'}
                    </Button>
                  )}
                  <button onClick={() => exportTheme(p.theme)} className="text-xs text-app-muted hover:text-app-fg transition-colors shrink-0">JSON</button>
                  <button onClick={() => exportThemeCss(p.theme)} className="text-xs text-app-muted hover:text-app-fg transition-colors shrink-0">CSS</button>
                  {!p.is_active && (
                    <button onClick={() => void handleDelete(p.id)} disabled={busy !== null}
                      className="text-xs text-crimson/60 hover:text-crimson transition-colors shrink-0">Delete</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══ DOCS TAB ════════════════════════════════════════════════════ */}
        {activeTab === 'docs' && (
          <div className="flex flex-col gap-6">
            <Section title="Brand Voice & Content Guidelines" description="Reference-only — not editable from this page.">
              <div className="flex flex-col gap-4 text-sm text-app-fg-2 leading-relaxed">
                <div>
                  <h4 className="font-semibold text-app-fg mb-1">Positioning</h4>
                  <p>
                    Sekretly is anti-algorithm, anti-generic-travel-content. We're "a friend in town" who shows you
                    "secret spots" that locals actually love. The product exists to "map your taste" through genuine
                    conversation, then match you with someone real — "curated by people, not feeds."
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-app-fg mb-1">Tone</h4>
                  <p>
                    Warm, unhurried, intelligent. Like a friend who happens to know the world deeply. Never salesy,
                    never generic, never breathless. We don't say "amazing," "fantastic," "incredible," or "perfect" —
                    those words mean nothing.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-app-fg mb-1">Content Rules</h4>
                  <ul className="list-disc pl-5 flex flex-col gap-1">
                    <li>Avoid superlatives and generic travel language</li>
                    <li>Use specific, sensory, evocative descriptions</li>
                    <li>Mirror the user's energy — if they're brief, be brief</li>
                    <li>Ask 1–2 questions max per message, let conversation breathe</li>
                    <li>Never mention the AI/bot nature of the conversation</li>
                  </ul>
                </div>
              </div>
            </Section>

            <Section title="What's Editable vs. Reference-Only" description="For anyone working on this project.">
              <div className="flex flex-col gap-4 text-sm text-app-fg-2 leading-relaxed">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-success/5 border border-success/20 rounded-xl p-4">
                    <h4 className="font-semibold text-success mb-2">Editable from this page</h4>
                    <ul className="list-disc pl-5 flex flex-col gap-1 text-xs">
                      <li>Accent colors (gold, violet, crimson, etc.)</li>
                      <li>Semantic colors (light & dark mode)</li>
                      <li>Border radius scale</li>
                      <li>Font families</li>
                      <li>Theme name and preset management</li>
                    </ul>
                  </div>
                  <div className="bg-crimson/5 border border-crimson/20 rounded-xl p-4">
                    <h4 className="font-semibold text-crimson mb-2">Requires code changes</h4>
                    <ul className="list-disc pl-5 flex flex-col gap-1 text-xs">
                      <li>Component structure, layout, and markup</li>
                      <li>Spacing scale (static in CSS, not themed)</li>
                      <li>Breakpoints and responsive behavior</li>
                      <li>Animation and transitions</li>
                      <li>Brand voice and content rules</li>
                      <li>Adding new token groups or components</li>
                    </ul>
                  </div>
                </div>
                <p className="text-xs text-app-muted">
                  The component catalog above imports and renders real production components — not copies.
                  Token changes made here propagate through CSS custom properties that those components consume via
                  Tailwind utilities. Structural changes (new variants, new components, layout) still require code changes
                  and a deploy.
                </p>
              </div>
            </Section>
          </div>
        )}
      </div>

      {/* ═══ DIFF / SAVE MODAL ════════════════════════════════════════════ */}
      {showDiff && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center" onClick={() => setShowDiff(false)}>
          <div className="bg-app-card md:border md:border-app-border rounded-t-3xl md:rounded-2xl w-full max-w-lg max-h-dvh-85 overflow-y-auto pb-safe-or-6" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-app-card z-10 px-5 pt-5 pb-3 border-b border-app-border flex items-center justify-between gap-3">
              <h3 className="text-lg font-semibold text-app-fg">Save theme changes</h3>
              <button onClick={() => setShowDiff(false)} className="w-8 h-8 rounded-full bg-app-bg border border-app-border text-app-muted flex items-center justify-center hover:text-app-fg shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                </svg>
              </button>
            </div>
            <div className="px-5 py-4 flex flex-col gap-4">
              <p className="text-sm text-app-muted">
                {changes.length} token{changes.length === 1 ? '' : 's'} will be updated. This creates a new preset and applies it to all users immediately.
              </p>
              <div className="flex flex-col gap-1 max-h-64 overflow-y-auto">
                {changes.map((c, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs py-1.5 border-b border-app-border last:border-0">
                    <span className="text-app-muted font-mono flex-1 truncate">{c.path.replace(/^\./, '')}</span>
                    {c.from.startsWith('#') || c.from.startsWith('rgba') ? (
                      <>
                        <span className="w-4 h-4 rounded border border-app-border shrink-0" style={{ background: c.from }} />
                        <span className="text-app-muted">→</span>
                        <span className="w-4 h-4 rounded border border-app-border shrink-0" style={{ background: c.to }} />
                      </>
                    ) : (
                      <>
                        <span className="text-crimson/70 line-through truncate max-w-[80px]">{c.from}</span>
                        <span className="text-app-muted">→</span>
                        <span className="text-success truncate max-w-[80px]">{c.to}</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <Button variant="ghost" onClick={() => setShowDiff(false)}>Cancel</Button>
                <Button onClick={() => void handleSave()} disabled={busy === 'save'}>
                  {busy === 'save' ? <Spinner size="sm" className="text-ink mr-2" /> : null}
                  Save & apply
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DesignSettings;
